// Результат сверки по единому чёрному списку. Три исхода, и они НЕ сводятся к «да/нет»
// (тз 5.8.2): точное совпадение блокирует продвижение, возможное требует явного
// подтверждения на оформлении, «чисто» — это ответ, а не отсутствие ответа.
//
// Отдельно и главное — правило 7. Есть четвёртое состояние, и оно не равно «чисто»:
// **сверки не было вовсе** (`blacklist_result === null`). Слипание этих двух фактов в одну
// зелёную метку означало бы, что человек считает непроверенного проверенным. Поэтому у
// «не сверялся» свой вид и свой текст.

import { RiShieldCheckLine, RiShieldFlashLine, RiShieldKeyholeLine } from '@remixicon/react';

import type { CheckResult } from '../api/candidates';
import * as Badge from '@/shared/ui/badge';
import { CHECK_RESULT_COLORS, CHECK_RESULT_TITLES } from '../dictionaries';

/** Метка в строке списка и в шапке карточки. `null` — сверки не было. */
export function BlacklistBadge({ result }: { result: CheckResult | null | undefined }) {
  if (result === null || result === undefined) {
    return (
      <Badge.Root variant='lighter' color='gray' size='medium'>
        Не сверялся
      </Badge.Root>
    );
  }
  return (
    <Badge.Root variant='lighter' color={CHECK_RESULT_COLORS[result]} size='medium'>
      {CHECK_RESULT_TITLES[result]}
    </Badge.Root>
  );
}

const ЗНАЧКИ = {
  clear: RiShieldCheckLine,
  exact: RiShieldFlashLine,
  possible: RiShieldKeyholeLine,
} as const;

/**
 * Что значит исход, когда объяснения сервера под рукой нет.
 *
 * Оно бывает не всегда: в карточке лежит только КОД прошлой сверки (`blacklist_result`), а
 * слова приезжают лишь в ответе на саму сверку. Без запасного текста человек видел бы красную
 * метку «Точное совпадение» и ни одного слова о том, что теперь делать, — то есть ровно ту
 * блокировку без причины и шага, которой не бывает (правило 10).
 *
 * Тексты держатся близко к серверным (`server/app/hiring/services/blacklist.py`) намеренно:
 * если они разойдутся, разойдутся в мелочи, а не в сути.
 */
const ЗАПАСНОЕ_ОБЪЯСНЕНИЕ: Readonly<Record<CheckResult, { что: string; шаг: string }>> = {
  clear: {
    что: 'В едином чёрном списке этот человек не найден.',
    шаг: 'Сверка повторится сама при оформлении: между проверкой и приёмом на работу список мог пополниться.',
  },
  exact: {
    что: 'Человек числится в едином чёрном списке: совпадение точное.',
    шаг: 'Дальше по воронке его не двигают. Решение по совпадению принимает ответственный за список, а не система: если это другой человек, запись надо снять либо поправить данные кандидата.',
  },
  possible: {
    что: 'Похожая запись в чёрном списке есть, но совпадение неточное.',
    шаг: 'Вести кандидата можно, а на оформлении понадобится явное подтверждение — оно останется в журнале сверок вместе с вашим именем. Посмотрите найденную запись заранее.',
  },
};

/**
 * Итог сверки в карточке кандидата: что нашли, когда и что теперь делать.
 *
 * Текст берётся из ответа сервера (`message`, `next_action`), а не пишется здесь заново:
 * причина отказа и следующий шаг — это решение предметной области, и второй их пересказ на
 * клиенте разойдётся с первым на первой же правке правил сверки.
 */
export function BlacklistResultCard({
  result,
  checkedAt,
  message,
  nextAction,
  reasonTitle,
}: {
  result: CheckResult | null | undefined;
  /** Когда сверяли. Пусто вместе с результатом — сверки не было. */
  checkedAt: string | null | undefined;
  message?: string;
  nextAction?: string;
  reasonTitle?: string | null;
}) {
  const Значок = result == null ? RiShieldKeyholeLine : ЗНАЧКИ[result];
  return (
    <div className='flex items-start gap-3 rounded-xl bg-bg-white-0 p-4 ring-1 ring-inset ring-stroke-soft-200'>
      <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-bg-weak-50'>
        <Значок className='size-4 text-text-soft-400' />
      </span>
      <div className='flex min-w-0 flex-col gap-1'>
        <span className='flex flex-wrap items-center gap-2'>
          <span className='text-label-sm text-text-strong-950'>Сверка по чёрному списку</span>
          <BlacklistBadge result={result} />
        </span>
        {result == null ? (
          <p className='text-paragraph-sm text-text-sub-600'>
            Этого человека по списку ещё не сверяли. Сверка идёт сама при приёме отклика и при
            оформлении; здесь её можно запустить руками — «чисто» без проверки система не считает.
          </p>
        ) : (
          <>
            <p className='text-paragraph-sm text-text-sub-600'>
              {message ?? ЗАПАСНОЕ_ОБЪЯСНЕНИЕ[result].что}
            </p>
            {reasonTitle != null && reasonTitle !== '' && (
              <p className='text-paragraph-xs text-text-sub-600'>Причина записи: {reasonTitle}</p>
            )}
            <p className='text-paragraph-xs text-text-sub-600'>
              {nextAction ?? ЗАПАСНОЕ_ОБЪЯСНЕНИЕ[result].шаг}
            </p>
            {checkedAt != null && (
              <p className='text-paragraph-xs text-text-sub-600'>
                Сверено {new Date(checkedAt).toLocaleString('ru-RU')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
